import { useEffect, useState } from "react";
import { ChevronDown, LockKeyhole, Shield } from "lucide-react";

import { isPast } from "@/lib/v3Pass";
import { AsideCard, StatusBanner } from "./v3";

// V3 building blocks for profile and Access Pass views (M18–M20, M82–M92, M110).
export const ShareAside = () => (<>
  <AsideCard icon={LockKeyhole} title="Nicht automatisch geteilt" text="Weitere Passfelder, Mitgliedschaften und private Nachrichten bleiben ausserhalb dieser Freigabe." testid="pass-aside-not-shared" />
  <AsideCard icon={Shield} tone="white" title="Keine Rollenfreigabe" text="Kontakt, Teilnahme und Organisationsrolle ersetzen die persönliche Entscheidung nicht." testid="pass-aside-no-role" />
</>);

export const PrivateBanner = ({ title = "Privat · Nur ich", text, testid = "access-pass-private" }) => <StatusBanner icon={LockKeyhole} title={title} text={text} testid={testid} />;

export const initialOf = (s) => (Array.from(String(s || "").trim())[0] || "V").toUpperCase();

export const FIELD_INPUT = "block min-h-[26px] w-full bg-transparent text-[14.5px] leading-[22px] text-v3-ink outline-none placeholder:text-v3-muted lg:text-[16px]";

// Card with a small label and a borderless control (M20 form, same look as DetailRow).
export function FieldCard({ id, label, error, hint, children, testid, as = "label" }) {
  const Label = as;
  return (
    <div className="mb-[11px]" data-testid={testid}>
      <div className={`rounded-[10px] border bg-white px-[16px] pb-[12px] pt-[10px] transition-colors focus-within:border-v3-magenta ${error ? "border-red-700" : "border-v3-line"}`}>
        <Label {...(as === "label" ? { htmlFor: id } : { id: `${id}-label` })} className="block text-[13px] font-bold leading-[18px] text-v3-muted lg:text-[14px] lg:leading-[19px]">{label}</Label>
        <div className="mt-[4px]">{children}</div>
      </div>
      {hint && <p id={`${id}-hint`} className="mt-[6px] text-[13px] text-v3-muted">{hint}</p>}
      {error && <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="mt-[6px] text-[13px] font-bold text-red-800">{error}</p>}
    </div>
  );
}

// M82: title row plus a native select that shows the current value.
export function SelectCard({ id, title, value, onChange, options, testid }) {
  return (
    <div className="relative rounded-[10px] border border-v3-line bg-white transition-colors focus-within:border-v3-magenta" data-testid={testid}>
      <label htmlFor={id} className="block px-[18px] pt-[13px] text-[16px] font-bold leading-[22px] text-v3-ink">{title}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-select`}
        className="block h-[52px] w-full cursor-pointer appearance-none rounded-b-[10px] bg-transparent pb-[14px] pl-[18px] pr-[52px] pt-[4px] text-[14px] text-v3-muted outline-none">
        {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-[22px] top-1/2 h-[16px] w-[16px] -translate-y-1/2 text-v3-muted" aria-hidden="true" />
    </div>
  );
}

// M19: expandable area card; the panel lists the sections with their saved state.
export function PassGroupCard({ icon: Icon, title, subtitle, open, onToggle, children, testid }) {
  return (
    <div className="rounded-[10px] border border-v3-line bg-white" data-testid={testid}>
      <button type="button" aria-expanded={open} aria-controls={`${testid}-panel`} onClick={onToggle} data-testid={`${testid}-toggle`}
        className="flex min-h-[100px] w-full items-start gap-[20px] rounded-[10px] px-[14px] pb-[20px] pt-[11px] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-magenta">
        <Icon className="mt-[13px] h-[28px] w-[28px] shrink-0 text-v3-magenta" strokeWidth={1.9} aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block break-words text-[17px] font-bold leading-[24px] text-v3-ink">{title}</span>
          <span className="mt-[14px] block text-[14px] leading-[20px] text-v3-muted">{subtitle}</span>
        </span>
        <ChevronDown className={`mr-[6px] h-[18px] w-[18px] shrink-0 self-center text-v3-muted transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && <div id={`${testid}-panel`} className="space-y-[14px] border-t border-v3-line px-[18px] pb-[18px] pt-[14px]" data-testid={`${testid}-panel`}>{children}</div>}
    </div>
  );
}

// QR from the Core's module rows (validated 0/1 strings); the text code is always shown next to it.
export function QrCode({ rows, label }) {
  const n = rows.length;
  const d = rows.flatMap((r, y) => Array.from(r).map((c, x) => (c === "1" ? `M${x + 4} ${y + 4}h1v1h-1z` : ""))).join("");
  return (
    <svg role="img" aria-label={label} viewBox={`0 0 ${n + 8} ${n + 8}`} shapeRendering="crispEdges" data-testid="pass-qr" className="h-[176px] w-[176px] rounded-[8px] border border-v3-line bg-white">
      <rect width={n + 8} height={n + 8} fill="#FFFFFF" /><path d={d} fill="#17004B" />
    </svg>
  );
}

// True once the given time has passed; re-renders exactly at expiry so values disappear without reload.
export function useExpired(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const wait = iso ? new Date(iso).getTime() - Date.now() : NaN;
    if (!Number.isFinite(wait) || wait <= 0 || wait > 2147483647) return undefined;
    const t = setTimeout(() => setNow(Date.now()), wait + 50);
    return () => clearTimeout(t);
  }, [iso]);
  return isPast(iso, now);
}
